<template>
	<!-- CZ: 주간 KPI. 예전 업무현황판(기간 자유·목표 합 달성률·신호등)을 대표 요구(2026-10)의 주간 KPI 로 바꿨다.
	     숫자 정의는 backend/util/kpiCalc.util.js 가 단일 출처이고, 보이는 범위는 kpiScope.util.js 가 정한다. -->
	<div class="content workboard">
		<div class="section">
			<div class="section-tit">
				주간 KPI
				<span class="sub">월요일 ~ 일요일 · 지난주 결과가 이번 주 신규 매수배정을 정합니다</span>
				<div class="right" v-if="viewer.isAdmin || viewer.canLead">
					<!-- 신규 매수문의 관리(master 와 매수배정 담당, DC). 상단 메뉴 폭이 1440px 에서 넘쳐 메뉴 대신 여기 둔다. -->
					<router-link class="btn btn-sm btn-primary" to="/LeadAdmin">신규 매수문의</router-link>
					<button
						v-if="viewer.isAdmin"
						type="button"
						class="btn btn-sm"
						v-bind:class="modes.meetingAll ? 'btn-danger' : 'btn-light'"
						:disabled="busy"
						@click="toggleMeeting('all')"
					>
						전체회의 모드 {{ modes.meetingAll ? '끄기' : '켜기' }}
					</button>
					<button
						v-if="viewer.isAdmin"
						type="button"
						class="btn btn-sm"
						v-bind:class="modes.meetingLeader ? 'btn-danger' : 'btn-light'"
						:disabled="busy"
						@click="toggleMeeting('leader')"
					>
						팀장회의 모드 {{ modes.meetingLeader ? '끄기' : '켜기' }}
					</button>
				</div>
			</div>
			<div class="tab-btn">
				<button type="button" class="btn tab" v-bind:class="{ active: tab === 'me' }" @click="setTab('me')">내 KPI</button>
				<button type="button" class="btn tab" v-bind:class="{ active: tab === 'board' }" @click="setTab('board')">직원 KPI</button>
				<button
					v-if="viewer.isAdmin || viewer.isLeader"
					type="button"
					class="btn tab"
					v-bind:class="{ active: tab === 'leaders' }"
					@click="setTab('leaders')"
				>
					팀장 비교
				</button>
				<button type="button" class="btn tab" v-bind:class="{ active: tab === 'report' }" @click="setTab('report')">월간·분기</button>
			</div>
			<div v-if="modes.meetingAll" class="wb-mode">
				전체회의 모드가 켜져 있습니다(오늘 {{ untilLabel(modes.meetingAllUntil) }}까지). 모든 직원의 KPI 숫자가 보이지만,
				고객 연락처·상담 내용·상세 기록은 평소 권한대로만 열립니다.
			</div>
			<div v-if="modes.meetingLeader" class="wb-mode">
				팀장회의 모드가 켜져 있습니다(오늘 {{ untilLabel(modes.meetingLeaderUntil) }}까지). 팀장은 다른 팀장과 팀의 KPI
				비교표를 볼 수 있습니다.
			</div>
			<div v-if="viewer.teamUnassigned" class="txt-c--red wb-note">
				※ 팀 배정이 없어 <b>본인 데이터만</b> 보입니다. 시스템관리 &gt; 팀 관리에서 팀을 지정하면 팀원 현황이 함께
				보입니다.
			</div>
		</div>

		<!-- 내 KPI -->
		<template v-if="tab === 'me'">
			<KpiCard @loaded="onMe" />
		</template>

		<!-- 주 고르기 (직원 KPI · 팀장 비교) -->
		<div class="section wb-weekbar" v-if="tab === 'board' || tab === 'leaders'">
			<button type="button" class="btn btn-sm btn-light" @click="goWeek(nav.prev)">‹ 이전 주</button>
			<b class="wb-weekbar__label">{{ weekLabel }}</b>
			<button type="button" class="btn btn-sm btn-light" :disabled="!nav.next" @click="goWeek(nav.next)">다음 주 ›</button>
			<button v-if="!nav.is_current" type="button" class="btn btn-sm btn-light" @click="goWeek(nav.this_week)">이번 주</button>
			<span class="wb-weekbar__state">
				<template v-if="closed">마감 {{ closedAt }} · 그때의 기준값으로 굳은 결과입니다</template>
				<template v-else-if="nav.is_current">진행 중 · 실시간 집계(이번 주 실적으로는 배정을 막지 않습니다)</template>
				<template v-else>마감 전 · 지금 기준값으로 계산한 값입니다</template>
			</span>
			<span class="wb-weekbar__policy">
				기준: 직원 전화 {{ policy.call_min_staff }}통·신규매물 {{ policy.new_min_staff }}건, 팀장 전화 {{ policy.call_min_leader }}통 ·
				{{ policy.session_minutes }}분 안에 이어진 같은 매물 기록은 1통 · 미팅·현장 1회 {{ policy.credit_meet_sell }}통 인정(목표의
				{{ policy.credit_cap_pct }}%까지)
			</span>
		</div>

		<!-- 직원 KPI -->
		<div class="section" v-if="tab === 'board'">
			<div class="section-tit">
				직원 KPI
				<span class="sub">숫자를 누르면 실제 기록이 열립니다 · 부재는 KPI 에 넣지 않고 따로 셉니다</span>
			</div>
			<div class="table-wrap">
				<table class="table wb-table">
					<tr>
						<th>직원</th>
						<th>팀/직급</th>
						<th>KPI</th>
						<th>달성률</th>
						<th title="통화완료 세션 수">실제 전화</th>
						<th title="전부 부재·미응답인 세션 수(KPI 제외)">부재</th>
						<th title="매도미팅·매수미팅·현장안내 인정분(상한 적용)">미팅·현장 인정</th>
						<th>신규매물</th>
						<th>매도미팅</th>
						<th>매수미팅</th>
						<th>현장</th>
						<th>협상</th>
						<th>계약</th>
						<th>브리핑</th>
						<th title="지금 시점의 14일 작업권 초과">기한초과</th>
						<th title="원본 작업내역 건수">작업내역</th>
						<th>판정</th>
						<th v-if="nav.is_current">신규배정</th>
					</tr>
					<tr v-for="r in boardRows" :key="'b_' + r.mem_uid" v-bind:class="{ 'wb-me': r.mem_uid === viewer.memUid }">
						<td class="txt-left"><b>{{ r.mem_name }}</b></td>
						<td class="wb-sub">{{ r.team_name || '-' }} / {{ r.rank_name || '-' }}</td>
						<td>
							<b>{{ r.call_total }}</b><span class="txt-c--grey"> / {{ r.target_call }}</span>
						</td>
						<td><b v-bind:class="rateClass(r)">{{ pct(r.call_rate) }}</b></td>
						<td v-bind:class="cellClass(r)" @click="drill(r, 'calls')">{{ r.calls }}</td>
						<td v-bind:class="cellClass(r)" @click="drill(r, 'missed')">{{ r.missed }}</td>
						<td>+{{ r.credit_applied }}</td>
						<td v-bind:class="cellClass(r)" @click="drill(r, 'new')">
							{{ r.new_cnt }}<span class="txt-c--grey"> / {{ r.target_new }}</span>
						</td>
						<td v-bind:class="cellClass(r)" @click="drill(r, 'meet_sell')">{{ r.meet_sell }}</td>
						<td v-bind:class="cellClass(r)" @click="drill(r, 'meet_buy')">{{ r.meet_buy }}</td>
						<td v-bind:class="cellClass(r)" @click="drill(r, 'site')">{{ r.site }}</td>
						<td v-bind:class="cellClass(r)" @click="drill(r, 'nego')">{{ r.nego }}</td>
						<td v-bind:class="cellClass(r)" @click="drill(r, 'contract')">{{ r.contract }}</td>
						<td v-bind:class="cellClass(r)" @click="drill(r, 'brief')">{{ r.brief }}</td>
						<td v-bind:class="[cellClass(r), { 'txt-c--red': r.over_cnt > 0 }]" @click="drill(r, 'over')">{{ r.over_cnt }}</td>
						<td v-bind:class="cellClass(r)" @click="drill(r, 'raw')">{{ r.raw_logs }}</td>
						<td :title="r.fail_reasons">
							<span v-if="r.pass_flag === 'Y'" class="wb-ok">충족</span>
							<span v-else-if="r.pass_flag === 'N'" class="txt-c--red">미달</span>
							<span v-else class="txt-c--grey">평가 제외</span>
						</td>
						<td v-if="nav.is_current" :title="assignTip(r)">
							<template v-if="r.assign">
								<span v-if="r.assign.status === 'PASS'" class="wb-ok">PASS</span>
								<span v-else-if="r.assign.status === 'HOLD'" class="txt-c--red">HOLD</span>
								<span v-else class="txt-c--grey">-</span>
							</template>
						</td>
					</tr>
					<tr v-if="boardRows.length" class="bg">
						<td colspan="2"><b>합계 ({{ boardRows.length }}명)</b></td>
						<td><b>{{ sum('call_total') }}</b></td>
						<td></td>
						<td><b>{{ sum('calls') }}</b></td>
						<td><b>{{ sum('missed') }}</b></td>
						<td><b>+{{ sum('credit_applied') }}</b></td>
						<td><b>{{ sum('new_cnt') }}</b></td>
						<td><b>{{ sum('meet_sell') }}</b></td>
						<td><b>{{ sum('meet_buy') }}</b></td>
						<td><b>{{ sum('site') }}</b></td>
						<td><b>{{ sum('nego') }}</b></td>
						<td><b>{{ sum('contract') }}</b></td>
						<td><b>{{ sum('brief') }}</b></td>
						<td><b>{{ sum('over_cnt') }}</b></td>
						<td><b>{{ sum('raw_logs') }}</b></td>
						<td>{{ boardRows.filter((r) => r.pass_flag === 'Y').length }}명 충족</td>
						<td v-if="nav.is_current"></td>
					</tr>
					<tr v-if="loaded.board && !boardRows.length">
						<td :colspan="nav.is_current ? 18 : 17">조회할 직원이 없습니다.</td>
					</tr>
				</table>
			</div>
		</div>

		<!-- 팀장 비교 (6절) -->
		<div class="section" v-if="tab === 'leaders'">
			<div class="section-tit">
				팀장 비교
				<span class="sub">팀 합계(팀장 포함) · 팀이 지정되지 않은 팀장은 본인 숫자만 보입니다</span>
			</div>
			<div class="table-wrap">
				<table class="table wb-table">
					<tr>
						<th rowspan="2">팀장</th>
						<th rowspan="2">팀</th>
						<th rowspan="2">팀장 본인 KPI</th>
						<th colspan="12">팀 합계</th>
					</tr>
					<tr>
						<th>전화</th>
						<th>매수고객 연락</th>
						<th>매수 통화</th>
						<th>브리핑</th>
						<th>매도미팅</th>
						<th>매수미팅</th>
						<th>현장</th>
						<th>협상</th>
						<th>계약</th>
						<th>신규매물</th>
						<th>기한초과</th>
						<th>KPI 달성률</th>
					</tr>
					<tr v-for="l in leaderRows" :key="'l_' + l.mem_uid">
						<td class="txt-left"><b>{{ l.mem_name }}</b></td>
						<td class="wb-sub">
							<template v-if="l.team_unassigned">팀 미지정</template>
							<template v-else>{{ l.team_name }} {{ l.team.member_cnt }}명</template>
						</td>
						<td>
							{{ l.call_total }} / {{ l.target_call }}
							<b v-bind:class="rateClass(l)">{{ pct(l.call_rate) }}</b>
						</td>
						<td>{{ l.team.calls }}</td>
						<td title="통화완료 상담을 남긴 매수고객 수">{{ l.team.contact_clients }}</td>
						<td>{{ l.team.calls_cli }}</td>
						<td>{{ l.team.brief }}</td>
						<td>{{ l.team.meet_sell }}</td>
						<td>{{ l.team.meet_buy }}</td>
						<td>{{ l.team.site }}</td>
						<td>{{ l.team.nego }}</td>
						<td>{{ l.team.contract }}</td>
						<td>{{ l.team.new_cnt }}</td>
						<td v-bind:class="{ 'txt-c--red': l.team.over_cnt > 0 }">{{ l.team.over_cnt }}</td>
						<td>
							<b>{{ pct(l.team.call_rate) }}</b>
							<span class="txt-c--grey d-block">충족 {{ l.team.pass_cnt }}/{{ l.team.eval_cnt }}명</span>
						</td>
					</tr>
					<tr v-if="loaded.leaders && !leaderRows.length">
						<td colspan="15">팀장 직급의 직원이 없습니다.</td>
					</tr>
				</table>
			</div>
		</div>

		<!-- 월간·분기 (18·19절) -->
		<template v-if="tab === 'report'">
			<div class="section">
				<div class="section-tit">
					월간·분기 업무현황
					<span class="sub">원자료 그대로 집계합니다. KPI 달성률은 마감된 주의 평균입니다</span>
				</div>
				<div class="search-top">
					<div class="left">
						<div class="segment wb-units">
							<button
								v-for="u in UNITS"
								:key="'u_' + u.value"
								type="button"
								class="btn btn-sm"
								v-bind:class="report.unit === u.value ? 'btn-primary' : 'btn-light'"
								@click="setUnit(u.value)"
							>
								{{ u.label }}
							</button>
						</div>
						<input type="date" class="form-control sm" v-model="report.sdate" @change="loadReport" />
						<span>~</span>
						<input type="date" class="form-control sm" v-model="report.edate" @change="loadReport" />
					</div>
				</div>
				<div class="wb-chips">
					<button
						v-for="m in METRICS"
						:key="'m_' + m.key"
						type="button"
						class="btn btn-xsm"
						v-bind:class="report.metric === m.key ? 'btn-secondary' : 'btn-outline-secondary'"
						@click="report.metric = m.key"
					>
						{{ m.label }}
					</button>
				</div>
				<div class="table-wrap">
					<table class="table wb-table">
						<tr>
							<th>직원</th>
							<th>팀</th>
							<th v-for="p in reportData.periods" :key="'ph_' + p">{{ periodLabel(p) }}</th>
							<th>{{ metricDef.rate ? '평균' : '합계' }}</th>
							<th>추이</th>
						</tr>
						<tr v-for="m in reportData.members" :key="'rm_' + m.mem_uid">
							<td class="txt-left"><b>{{ m.mem_name }}</b> <span class="txt-c--grey">{{ m.rank_name || '' }}</span></td>
							<td class="wb-sub">{{ m.team_name || '-' }}</td>
							<td v-for="p in reportData.periods" :key="'pc_' + m.mem_uid + p">{{ cellText(p, m.mem_uid) }}</td>
							<td><b>{{ rowTotal(m.mem_uid) }}</b></td>
							<td class="wb-spark-cell">
								<svg class="wb-spark" viewBox="0 0 100 24" preserveAspectRatio="none">
									<polyline :points="sparkPoints(m.mem_uid)" />
								</svg>
							</td>
						</tr>
						<tr v-if="reportData.members.length && !metricDef.rate" class="bg">
							<td colspan="2"><b>합계</b></td>
							<td v-for="p in reportData.periods" :key="'pt_' + p"><b>{{ colTotal(p) }}</b></td>
							<td><b>{{ reportData.periods.reduce((a, p) => a + colTotal(p), 0) }}</b></td>
							<td></td>
						</tr>
						<tr v-if="loaded.report && !reportData.members.length">
							<td :colspan="reportData.periods.length + 4">조회할 직원이 없습니다.</td>
						</tr>
					</table>
				</div>
			</div>

			<div class="section" v-if="reportData.funnel">
				<div class="section-tit">
					매수고객 단계
					<span class="sub">이 기간에 등록된 매수고객이 지금까지 어느 단계까지 갔는지 · {{ viewer.isAdmin ? '전체' : '내 팀 담당 고객' }}</span>
				</div>
				<div class="table-wrap">
					<table class="table wb-table wb-funnel">
						<tr>
							<th v-for="f in FUNNEL" :key="'fh_' + f.key">{{ f.label }}</th>
						</tr>
						<tr>
							<td v-for="(f, i) in FUNNEL" :key="'fv_' + f.key">
								<b>{{ reportData.funnel[f.key] }}</b>
								<span v-if="i > 0" class="txt-c--grey d-block">등록 대비 {{ funnelPct(f.key) }}</span>
							</td>
						</tr>
					</table>
				</div>
				<div class="wb-note" v-if="reportData.funnel.leads && reportData.funnel.leads.length">
					같은 기간 신규 매수문의:
					<span v-for="l in reportData.funnel.leads" :key="'ls_' + l.source" class="wb-lead-src">{{ l.label }} {{ l.count }}건</span>
				</div>
			</div>
		</template>

		<!-- 드릴다운 -->
		<div v-if="drillView" class="popup-wrap">
			<div class="dim" @click="drillView = null"></div>
			<div class="popup md">
				<div class="popup-tit">
					{{ drillView.title }}
					<button type="button" class="btn btn-close" @click="drillView = null"><b-icon-x-lg /></button>
				</div>
				<div class="popup-con">
					<div class="wb-drill-desc">
						<b>{{ drillView.descTitle }}</b> {{ drillView.desc }}
					</div>
					<div class="table-wrap wb-drill-wrap">
						<table class="table sm click">
							<template v-if="drillView.sessionKind">
								<tr>
									<th>시각</th>
									<th>대상</th>
									<th>기록</th>
									<th>결과</th>
								</tr>
								<template v-for="(s, i) in drillView.list" :key="'ds_' + i">
									<tr @click="openTarget(s)">
										<td class="wb-nowrap">{{ s.start }}<template v-if="s.end !== s.start"> ~ {{ s.end.slice(6) }}</template></td>
										<td class="txt-left">
											<template v-if="s.building_uid">{{ s.building_name || '-' }} <span class="txt-c--grey">{{ s.jibun_addr }}</span></template>
											<template v-else-if="s.client_uid || s.client_name">{{ s.client_name || '-' }} <span class="txt-c--grey">매수고객</span></template>
										</td>
										<td>{{ s.record_cnt }}건</td>
										<td v-if="!s.kind.endsWith('_call')">-</td>
										<td v-else>
											<span v-if="s.done" class="wb-ok">통화완료</span>
											<span v-if="s.done && s.missed" class="txt-c--grey"> · </span>
											<span v-if="s.missed || !s.done" class="txt-c--red">부재</span>
										</td>
									</tr>
									<tr v-for="(rc, j) in s.records.filter((x) => x.memo)" :key="'dr_' + i + '_' + j" class="wb-drill-rec">
										<td>{{ rc.at.slice(6) }}</td>
										<td colspan="3" class="txt-left">
											<span v-if="rc.result === 'missed'" class="txt-c--red">[부재]</span> {{ rc.memo }}
										</td>
									</tr>
								</template>
							</template>
							<template v-else>
								<tr>
									<th v-for="c in drillView.cols" :key="'dc_' + c.key">{{ c.label }}</th>
								</tr>
								<tr v-for="(d, i) in drillView.list" :key="'dl_' + i" @click="openTarget(d)">
									<td v-for="c in drillView.cols" :key="'dv_' + c.key + i" v-bind:class="{ 'txt-left': c.left }">
										<template v-if="c.key === 'result'">
											<span v-if="d.result === 'missed'" class="txt-c--red">부재</span>
											<span v-else-if="d.result === 'done'">통화완료</span>
										</template>
										<template v-else-if="c.key === 'sell_status'">{{ $ITEM_STATE[d.sell_status] || d.sell_status || '-' }}</template>
										<template v-else>{{ d[c.key] == null || d[c.key] === '' ? '-' : d[c.key] }}</template>
									</td>
								</tr>
							</template>
							<tr v-if="!drillView.list.length">
								<td :colspan="drillView.sessionKind ? 4 : drillView.cols.length">내역이 없습니다.</td>
							</tr>
						</table>
					</div>
					<div class="btn-wrap">
						<button type="button" class="btn btn-sm btn-secondary" @click="drillView = null">닫기</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import KpiCard from '@/components/KpiCard.vue';

const UNITS = [
	{ value: 'month', label: '월간' },
	{ value: 'quarter', label: '분기' },
	{ value: 'week', label: '주간' },
];
const METRICS = [
	{ key: 'calls_bld', label: '전화' },
	{ key: 'missed', label: '부재' },
	{ key: 'new_cnt', label: '신규매물' },
	{ key: 'meet_sell', label: '매도미팅' },
	{ key: 'meet_buy', label: '매수미팅' },
	{ key: 'site', label: '현장안내' },
	{ key: 'nego', label: '협상' },
	{ key: 'contract', label: '계약' },
	{ key: 'brief', label: '브리핑' },
	{ key: 'calls_cli', label: '매수 통화' },
	{ key: 'contact_clients', label: '매수고객 연락' },
	{ key: 'kpi_rate', label: 'KPI 달성률', rate: true },
];
const FUNNEL = [
	{ key: 'registered', label: '등록' },
	{ key: 'consult', label: '상담' },
	{ key: 'brief', label: '브리핑' },
	{ key: 'meet', label: '미팅·현장' },
	{ key: 'nego', label: '협상' },
	{ key: 'contract', label: '계약' },
];
const DRILL = {
	calls: { title: '실제 전화', desc: '통화완료가 하나라도 있는 통화 묶음입니다. 같은 매물에 이어 적은 기록은 한 통으로 셉니다.', session: true },
	missed: { title: '부재·미응답', desc: '기록이 전부 부재인 통화 묶음입니다. KPI 에는 넣지 않습니다.', session: true },
	meet_sell: { title: '매도미팅', desc: '매물 작업내역 중 미팅으로 분류된 기록입니다(같은 매물 연속 기록은 1회).', session: true },
	meet_buy: { title: '매수미팅', desc: '매수고객 상담 중 미팅으로 분류된 기록입니다.', session: true },
	site: { title: '현장안내', desc: '매수고객 상담 중 현장안내로 분류된 기록입니다.', session: true },
	nego: { title: '협상', desc: '매수고객 상담 중 협상으로 분류된 기록입니다.', session: true },
	brief: {
		title: '브리핑',
		desc: '매수고객 브리핑을 대기에서 완료로 바꾼 기록입니다.',
		cols: [{ key: 'at', label: '완료 시각' }, { key: 'client_name', label: '고객' }, { key: 'building_name', label: '매물', left: true }],
	},
	contract: {
		title: '계약',
		desc: '계약관리에 등록된 계약(취소·삭제 제외) 중 이 직원이 참여한 건입니다. 계약일 기준입니다.',
		cols: [{ key: 'at', label: '계약일' }, { key: 'title', label: '계약', left: true }, { key: 'building_name', label: '매물', left: true }],
	},
	new: {
		title: '신규매물',
		desc: '매물 상태가 준비에서 매물로 처음 바뀐 건입니다. 매물마다 한 번만 셉니다.',
		cols: [{ key: 'at', label: '시각' }, { key: 'building_name', label: '매물', left: true }, { key: 'jibun_addr', label: '주소', left: true }, { key: 'sell_status', label: '지금 상태' }],
	},
	raw: {
		title: '작업내역 원본',
		desc: '이 주에 남긴 매물 작업내역 전부입니다. 원본은 합치거나 지우지 않습니다.',
		cols: [{ key: 'at', label: '시각' }, { key: 'type_name', label: '구분' }, { key: 'building_name', label: '매물', left: true }, { key: 'result', label: '결과' }, { key: 'memo', label: '내용', left: true }],
	},
	over: {
		title: '기한초과 작업',
		desc: '작업하기로 잡은 14일 작업권의 기한이 지났는데 아직 마무리되지 않은 매물입니다(지금 시점 기준).',
		cols: [{ key: 'building_name', label: '매물', left: true }, { key: 'jibun_addr', label: '주소', left: true }, { key: 'edate', label: '작업기한' }],
	},
};

function addDays(ymd, n) {
	const d = new Date(ymd + 'T00:00:00Z');
	d.setUTCDate(d.getUTCDate() + n);
	return d.toISOString().slice(0, 10);
}

export default {
	name: 'WorkBoard',
	components: { KpiCard },
	data() {
		return {
			UNITS,
			METRICS,
			FUNNEL,
			tab: 'me',
			busy: false,
			week: '',
			nav: { prev: '', next: null, this_week: '', is_current: true },
			closed: false,
			closedAt: '',
			weekEnd: '',
			policy: {},
			viewer: { memUid: null, isAdmin: false, isLeader: false, canLead: false, teamUnassigned: false },
			modes: { meetingAll: false, meetingLeader: false },
			boardRows: [],
			leaderRows: [],
			loaded: { board: false, leaders: false, report: false },
			report: { unit: 'month', sdate: '', edate: '', metric: 'calls_bld' },
			reportData: { periods: [], members: [], cells: {}, funnel: null },
			drillView: null,
		};
	},
	computed: {
		weekLabel() {
			if (!this.week) return '';
			return `${this.week} ~ ${this.weekEnd || addDays(this.week, 6)}`;
		},
		metricDef() {
			return METRICS.find((m) => m.key === this.report.metric) || METRICS[0];
		},
	},
	created() {
		const q = this.$route.query || {};
		if (['me', 'board', 'leaders', 'report'].includes(q.tab)) this.tab = q.tab;
		const y = new Date().getFullYear();
		this.report.sdate = `${y}-01-01`;
		this.report.edate = new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);
		// 화면 권한(관리자·팀장 버튼)과 회의 모드는 직원 KPI 표를 한 번 불러 정한다.
		this.loadBoard(q.week || '');
		if (this.tab === 'leaders') this.loadLeaders();
		if (this.tab === 'report') this.loadReport();
	},
	methods: {
		pct(v) {
			return v == null ? '-' : `${v}%`;
		},
		untilLabel(s) {
			return s ? s.slice(11, 16) : '';
		},
		setTab(t) {
			this.tab = t;
			if (t === 'board') this.loadBoard(this.week);
			if (t === 'leaders') this.loadLeaders();
			if (t === 'report' && !this.loaded.report) this.loadReport();
		},
		onMe(re) {
			if (re && re.viewer) this.viewer = re.viewer;
			if (re && re.modes) this.modes = re.modes;
		},
		applyWeek(re) {
			this.week = re.week_start;
			this.weekEnd = re.week_end;
			this.closed = !!re.closed;
			this.closedAt = re.closed_at || '';
			this.nav = re.nav;
			this.policy = re.policy || {};
			this.viewer = re.viewer || this.viewer;
			this.modes = re.modes || this.modes;
		},
		async loadBoard(week) {
			const re = await this.$apiGET('/admin/api/kpi/board' + (week ? '?week=' + week : ''));
			if (!re || !re.rows) return;
			this.applyWeek(re);
			this.boardRows = re.rows;
			this.loaded.board = true;
		},
		async loadLeaders() {
			const re = await this.$apiGET('/admin/api/kpi/leaders' + (this.week ? '?week=' + this.week : ''));
			if (!re || !re.rows) return;
			this.applyWeek(re);
			this.leaderRows = re.rows;
			this.loaded.leaders = true;
		},
		goWeek(w) {
			if (!w) return;
			this.week = w;
			if (this.tab === 'leaders') this.loadLeaders();
			else this.loadBoard(w);
		},
		async toggleMeeting(mode) {
			const on = mode === 'all' ? !this.modes.meetingAll : !this.modes.meetingLeader;
			const label = mode === 'all' ? '전체회의' : '팀장회의';
			const msg = on
				? `${label} 모드를 켭니다. ${mode === 'all' ? '모든 직원이 전 직원의 KPI 숫자를' : '팀장이 다른 팀장·팀의 KPI 를'} 볼 수 있게 되고, 오늘 자정에 자동으로 꺼집니다. 고객 연락처·상담 내용은 열리지 않습니다.`
				: `${label} 모드를 끕니다.`;
			if (!confirm(msg)) return;
			this.busy = true;
			try {
				const re = await this.$apiPOST('/admin/api/kpi/meeting', { mode, on });
				if (!re || !re.ok) return;
				this.modes = re;
			} finally {
				this.busy = false;
			}
		},
		rateClass(r) {
			const v = Number(r.call_rate);
			if (r.call_rate == null) return 'txt-c--grey';
			if (v >= 100) return 'wb-ok';
			if (v >= 70) return 'wb-warn';
			return 'txt-c--red';
		},
		cellClass(r) {
			return r.can_drill ? 'txt-c--blue wb-click' : '';
		},
		assignTip(r) {
			if (!r.assign || !r.assign.reasons || !r.assign.reasons.length) return '';
			return r.assign.reasons.map((x) => x.text).join('\n');
		},
		sum(k) {
			return this.boardRows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
		},
		async drill(row, metric) {
			if (!row.can_drill) return;
			const def = DRILL[metric];
			const re = await this.$apiGET(`/admin/api/kpi/drill?memUid=${row.mem_uid}&metric=${metric}&week=${this.week}`);
			if (!re || !re.list) return;
			this.drillView = {
				title: `${row.mem_name} · ${def.title}`,
				descTitle: def.title,
				desc: def.desc,
				sessionKind: !!def.session,
				cols: def.cols || [],
				list: re.list,
			};
		},
		openTarget(d) {
			const w = window.screen.availWidth;
			const h = window.screen.availHeight;
			const attr = `width=${w}, height=${h}, resizable=no, status=no`;
			if (d.building_uid) window.open('/MapDetail/' + d.building_uid, '', attr);
			else if (d.client_uid) window.open('/UserDetail/' + d.client_uid, '', attr);
		},
		// ── 월간·분기 ──
		setUnit(u) {
			this.report.unit = u;
			this.loadReport();
		},
		async loadReport() {
			const q = new URLSearchParams({ unit: this.report.unit, sdate: this.report.sdate, edate: this.report.edate });
			const re = await this.$apiGET('/admin/api/kpi/report?' + q.toString());
			if (!re || !re.periods) return;
			this.reportData = re;
			if (re.viewer) this.viewer = re.viewer;
			this.loaded.report = true;
		},
		periodLabel(p) {
			if (this.report.unit === 'month') return `${p.slice(2, 4)}.${p.slice(5, 7)}`;
			if (this.report.unit === 'week') return p.slice(5);
			return p;
		},
		cellVal(p, memUid) {
			const c = (this.reportData.cells[p] || {})[memUid];
			if (!c) return null;
			return c[this.report.metric];
		},
		cellText(p, memUid) {
			const v = this.cellVal(p, memUid);
			if (this.metricDef.rate) return v == null ? '-' : `${v}%`;
			return v || 0;
		},
		rowTotal(memUid) {
			const vals = this.reportData.periods.map((p) => this.cellVal(p, memUid));
			if (this.metricDef.rate) {
				const ok = vals.filter((v) => v != null);
				return ok.length ? `${Math.round((ok.reduce((a, b) => a + b, 0) / ok.length) * 10) / 10}%` : '-';
			}
			return vals.reduce((a, v) => a + (Number(v) || 0), 0);
		},
		colTotal(p) {
			return this.reportData.members.reduce((a, m) => a + (Number(this.cellVal(p, m.mem_uid)) || 0), 0);
		},
		sparkPoints(memUid) {
			const ps = this.reportData.periods;
			if (ps.length < 2) return '';
			const vals = ps.map((p) => Number(this.cellVal(p, memUid)) || 0);
			const max = Math.max(1, ...vals);
			return vals.map((v, i) => `${(i / (ps.length - 1)) * 100},${22 - (v / max) * 20}`).join(' ');
		},
		funnelPct(k) {
			const f = this.reportData.funnel;
			if (!f || !f.registered) return '-';
			return `${Math.round((f[k] / f.registered) * 1000) / 10}%`;
		},
	},
};
</script>

<style lang="scss">
// 색·간격·타이포 값의 출처는 DESIGN.md 하나뿐이다.
@import "@/assets/css/design-tokens.scss";

.workboard {
	// 공통 규칙 .table td(가운데 정렬)가 단일 클래스 .txt-left 보다 앞선다.
	.table td.txt-left {
		text-align: left;
	}
	.wb-mode {
		@include dt-type-dense;
		margin-top: $dt-spacing-cell-x;
		padding: $dt-spacing-cell-head $dt-spacing-cell-x;
		border-left: 3px solid $dt-color-danger;
		background: $dt-color-surface-sunken;
		color: $dt-color-ink-muted;
	}
	.wb-note {
		@include dt-type-dense;
		margin-top: $dt-spacing-cell-y;
	}
	.wb-weekbar {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px;
		&__label {
			font-size: 16px;
			margin: 0 6px;
		}
		&__state {
			@include dt-type-dense;
			color: $dt-color-ink-muted;
			margin-left: 8px;
		}
		&__policy {
			@include dt-type-dense;
			color: $dt-color-ink-faint;
			flex-basis: 100%;
		}
	}
	.wb-table {
		td {
			height: 40px;
			white-space: nowrap;
		}
		.wb-sub {
			@include dt-type-dense;
			color: $dt-color-ink-faint;
			white-space: nowrap;
		}
		.wb-click {
			cursor: pointer;
		}
		tr.wb-me td {
			background: $dt-color-surface-sunken;
		}
	}
	.wb-ok {
		color: $dt-color-success-ink;
		font-weight: 600;
	}
	.wb-warn {
		color: $dt-color-warning-ink;
	}
	.wb-units {
		margin-right: 8px;
	}
	.wb-chips {
		display: flex;
		flex-wrap: wrap;
		gap: 4px;
		margin: $dt-spacing-cell-x 0;
	}
	.wb-spark-cell {
		width: 110px;
	}
	.wb-spark {
		width: 100px;
		height: 24px;
		polyline {
			fill: none;
			stroke: $dt-color-primary;
			stroke-width: 1.5;
			vector-effect: non-scaling-stroke;
		}
	}
	.wb-funnel td {
		height: 56px;
		b {
			font-size: 18px;
		}
	}
	.wb-lead-src {
		margin-left: 10px;
	}
	.wb-drill-desc {
		@include dt-type-dense;
		margin: 0 0 $dt-spacing-cell-x;
		padding: $dt-spacing-cell-head $dt-spacing-cell-x;
		background: $dt-color-surface-sunken;
		border-left: 3px solid $dt-color-primary;
		color: $dt-color-ink-muted;
		b {
			color: $dt-color-ink;
			margin-right: 4px;
		}
	}
	.wb-drill-wrap {
		max-height: 60vh;
		overflow-y: auto;
	}
	.wb-drill-rec td {
		@include dt-type-dense;
		color: $dt-color-ink-muted;
		background: $dt-color-surface-sunken;
		white-space: normal;
	}
	.wb-nowrap {
		white-space: nowrap;
	}
}
@media print {
	.header,
	.workboard .tab-btn,
	.workboard .section-tit .right,
	.workboard .wb-weekbar button,
	.popup-wrap {
		display: none !important;
	}
}
</style>
